/**
 * Innholdsdialog («hva ligger her?») for en reol eller én enkeltlokasjon.
 * Åpnes fra egenskaps-panelet (Vis innhold) og styres via reducer-feltet
 * `contentsDialogFor`. Montert i editor-roten slik at den virker i både 2D og 3D.
 * Samme overlay-mønster som CalibrationDialog, men bredere (tabellinnhold).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, MoveRight, Package, PackageOpen, X } from 'lucide-react';
import { useLayoutEditor } from '../contexts/LayoutEditorContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import {
  warehouseLayoutService,
  ServerRackContents,
} from '../../../../../services/warehouseLayoutService';

/** Norsk tallformat for antall (maks 2 desimaler, ingen unødige nuller) */
const quantityFormat = new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 2 });

/** Kort norsk datoformat for utløpsdato */
const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('nb-NO');
};

const ContentsDialog: React.FC = () => {
  const { state, dispatch, warehouseId } = useLayoutEditor();
  const { t } = useTranslation();
  const target = state.contentsDialogFor;

  const [contents, setContents] = useState<ServerRackContents | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = () => dispatch({ type: 'CLOSE_CONTENTS_DIALOG' });

  // Lukk dialogen hvis seleksjonen som åpnet den forsvinner
  const stillSelected =
    !target ||
    (target.kind === 'rack'
      ? state.editorState.selectedRacks.has(target.code)
      : state.selectedBinCode === target.code);

  useEffect(() => {
    if (target && !stillSelected) {
      dispatch({ type: 'CLOSE_CONTENTS_DIALOG' });
    }
  }, [target, stillSelected, dispatch]);

  // Hent innhold ved åpning (ingen cache — dataene er ferskvare)
  useEffect(() => {
    if (!target) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setContents(null);
    warehouseLayoutService.getRackContents(warehouseId, target.code)
      .then(result => {
        if (!cancelled) setContents(result);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [target, warehouseId]);

  // Esc lukker dialogen (window-nivå slik at det virker i både 2D og 3D)
  useEffect(() => {
    if (!target) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        dispatch({ type: 'CLOSE_CONTENTS_DIALOG' });
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [target, dispatch]);

  // Skjul kolonner der alle rader er tomme — hold tabellen ren
  const visibleColumns = useMemo(() => {
    const rows = contents?.rows ?? [];
    return {
      batch: rows.some(r => !!r.batchNo),
      serial: rows.some(r => !!r.serialNo),
      expiry: rows.some(r => !!r.expiryDate),
    };
  }, [contents]);

  if (!target) return null;

  const title = target.kind === 'rack'
    ? t('manager.modules.warehouseLayout.contents.rackTitle', { '0': target.code }, `Innhold: Reol ${target.code}`)
    : t('manager.modules.warehouseLayout.contents.binTitle', { '0': target.code }, `Innhold: ${target.code}`);

  return (
    <div
      className="absolute inset-0 z-20 flex items-center justify-center bg-black/40"
      onMouseDown={e => {
        // Klikk utenfor dialogboksen lukker
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className="w-[640px] max-w-[92%] max-h-[80%] bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-2 p-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <Package className="w-5 h-5 text-blue-600 flex-shrink-0" />
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 truncate">{title}</h3>
            {contents && (
              <p className="text-xs text-gray-500">
                {t(
                  'manager.modules.warehouseLayout.contents.subtitle',
                  { '0': contents.binsWithStock, '1': contents.binCount },
                  `${contents.binsWithStock} av ${contents.binCount} lokasjoner i bruk`
                )}
              </p>
            )}
          </div>
          <button
            onClick={close}
            className="ml-auto p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0"
            title={t('common.buttons.close', {}, 'Lukk')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Innhold */}
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center gap-2 py-12 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
              <span className="text-sm">
                {t('manager.modules.warehouseLayout.contents.loading', {}, 'Henter innhold...')}
              </span>
            </div>
          )}

          {!loading && error && (
            <div className="py-10 px-6 text-center">
              <p className="text-sm text-red-600">
                {t('manager.modules.warehouseLayout.contents.loadFailed', {}, 'Kunne ikke hente innhold')}
              </p>
              <p className="text-xs text-gray-400 mt-1">{error}</p>
            </div>
          )}

          {!loading && !error && contents && contents.rows.length === 0 && (
            <div className="py-12 text-center text-gray-400">
              <PackageOpen className="w-10 h-10 mx-auto mb-2" />
              <p className="text-sm">
                {t('manager.modules.warehouseLayout.contents.empty', {}, 'Ingen beholdning her')}
              </p>
            </div>
          )}

          {!loading && !error && contents && contents.rows.length > 0 && (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white shadow-[0_1px_0_0_#e5e7eb]">
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <th className="px-4 py-2">
                    {t('manager.modules.warehouseLayout.contents.colLocation', {}, 'Lokasjon')}
                  </th>
                  <th className="px-2 py-2">
                    {t('manager.modules.warehouseLayout.contents.colItem', {}, 'Vare')}
                  </th>
                  <th className="px-2 py-2">
                    {t('manager.modules.warehouseLayout.contents.colDescription', {}, 'Beskrivelse')}
                  </th>
                  <th className="px-2 py-2 text-right">
                    {t('manager.modules.warehouseLayout.contents.colQuantity', {}, 'Antall')}
                  </th>
                  {visibleColumns.batch && (
                    <th className="px-2 py-2">
                      {t('manager.modules.warehouseLayout.contents.colBatch', {}, 'Batch')}
                    </th>
                  )}
                  {visibleColumns.serial && (
                    <th className="px-2 py-2">
                      {t('manager.modules.warehouseLayout.contents.colSerial', {}, 'Serienr')}
                    </th>
                  )}
                  {visibleColumns.expiry && (
                    <th className="px-4 py-2">
                      {t('manager.modules.warehouseLayout.contents.colExpiry', {}, 'Utløpsdato')}
                    </th>
                  )}
                  {/* «Flytt herfra»-kolonne */}
                  <th className="px-4 py-2">
                    <span className="sr-only">
                      {t('manager.modules.warehouseLayout.move.moveButton', {}, 'Flytt')}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {contents.rows.map((row, index) => (
                  <tr
                    key={`${row.binCode}-${row.itemNo}-${row.batchNo ?? ''}-${row.serialNo ?? ''}-${index}`}
                    className="border-t border-gray-100 hover:bg-gray-50"
                  >
                    <td className="px-4 py-1.5 font-mono text-gray-700 whitespace-nowrap">{row.binCode}</td>
                    <td className="px-2 py-1.5 font-mono text-gray-900 whitespace-nowrap">{row.itemNo}</td>
                    <td className="px-2 py-1.5 text-gray-600 max-w-[180px] truncate" title={row.itemDescription ?? undefined}>
                      {row.itemDescription ?? ''}
                    </td>
                    <td className="px-2 py-1.5 text-right font-medium text-gray-900 whitespace-nowrap">
                      {quantityFormat.format(row.quantity)}
                    </td>
                    {visibleColumns.batch && (
                      <td className="px-2 py-1.5 text-gray-600 whitespace-nowrap">{row.batchNo ?? ''}</td>
                    )}
                    {visibleColumns.serial && (
                      <td className="px-2 py-1.5 text-gray-600 whitespace-nowrap">{row.serialNo ?? ''}</td>
                    )}
                    {visibleColumns.expiry && (
                      <td className="px-4 py-1.5 text-gray-600 whitespace-nowrap">{formatDate(row.expiryDate)}</td>
                    )}
                    {/* Start «Flytt herfra»-flyten for denne raden (lukker dialogen, bytter til 2D) */}
                    <td className="px-4 py-1.5 text-right whitespace-nowrap">
                      <button
                        onClick={() =>
                          dispatch({
                            type: 'START_MOVE',
                            fromBinCode: row.binCode,
                            itemNo: row.itemNo,
                            itemDescription: row.itemDescription ?? null,
                            maxQuantity: row.quantity,
                          })
                        }
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md hover:bg-emerald-100 transition-colors"
                        title={t('manager.modules.warehouseLayout.move.moveButtonTooltip', {}, 'Flytt herfra: velg mållokasjon i 2D')}
                      >
                        <MoveRight className="w-3.5 h-3.5" />
                        {t('manager.modules.warehouseLayout.move.moveButton', {}, 'Flytt')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContentsDialog;
